"use client";

import { useState } from "react";
import {
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { PhysicsPlot } from "@/components/tools/physics-waves/plot";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  capacitorVoltage,
  cornerFrequency,
  rcCurrent,
  rcCurve,
  timeConstant,
  timeToFraction,
} from "@/lib/tool-kit/physics-waves/capacitor";
import {
  axisPrefix,
  type NumberFormatter,
  shiftExp,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  FARAD_UNITS,
  findUnit,
  OHM_UNITS,
  SECOND_UNITS,
  toBase,
  VOLT_UNITS,
} from "@/lib/tool-kit/physics-waves/units";

export interface RcStrings {
  unitFor: string;
  enterAll: string;
  needPositive: string;
  working: string;
  rcResistance: string;
  rcCapacitance: string;
  rcSource: string;
  rcTime: string;
  rcDirection: string;
  rcCharging: string;
  rcDischarging: string;
  rcTau: string;
  rcCorner: string;
  rcVoltageAt: string;
  rcCurrentAt: string;
  rcPercent: string;
  rcMilestones: string;
  rcColumnTime: string;
  rcColumnVoltage: string;
  rcColumnPercent: string;
  rcColumnWhen: string;
  rcTauRow: string;
  rcHalfRow: string;
  rcNinetyRow: string;
  rcNinetyNineRow: string;
  rcStepTau: string;
  rcStepVoltageCharge: string;
  rcStepVoltageDischarge: string;
  rcStepCurrent: string;
  rcStepCorner: string;
  rcGraphLabel: string;
  rcGraphDescription: string;
  rcGraphX: string;
  rcGraphY: string;
  rcTimeBeyond: string;
  rcOneTau: string;
}

interface Entry {
  value: string;
  unit: string;
}

const SPANS = 5;
const STEPS = 100;
const WHOLE_TAUS = [1, 2, 3, 4, 5];

function read(entry: Entry, units: typeof OHM_UNITS): number | null {
  const parsed = parseNumberInput(entry.value);
  return parsed === null ? null : toBase(parsed, findUnit(units, entry.unit));
}

function percentText(fmt: NumberFormatter, fraction: number): string {
  return `${fmt(fraction * 100, { maximumFractionDigits: 1 })}%`;
}

function Graph({
  source,
  tau,
  charging,
  at,
  strings,
  fmt,
  sig,
}: {
  source: number;
  tau: number;
  charging: boolean;
  at: number | null;
  strings: RcStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const xPrefix = axisPrefix(SPANS * tau);
  const yPrefix = axisPrefix(source);
  const toX = (v: number) => shiftExp(v, -xPrefix.exp);
  const toY = (v: number) => shiftExp(v, -yPrefix.exp);
  const curve = rcCurve(source, tau, charging, SPANS, STEPS).map(
    ([x, y]) => [toX(x), toY(y)] as const
  );
  const dots = [
    {
      x: toX(tau),
      y: toY(capacitorVoltage(source, tau, tau, charging)),
      label: strings.rcOneTau,
    },
  ];
  if (at !== null && at > 0 && at <= SPANS * tau) {
    dots.push({
      x: toX(at),
      y: toY(capacitorVoltage(source, tau, at, charging)),
      label: withSiPrefix(
        fmt,
        capacitorVoltage(source, tau, at, charging),
        "V",
        sig
      ),
    });
  }
  return (
    <PhysicsPlot
      curves={[{ points: curve }]}
      description={t(strings.rcGraphDescription, {
        tau: withSiPrefix(fmt, tau, "s", sig),
      })}
      dots={dots}
      formatX={(v) => fmt(v, { maximumFractionDigits: 6 })}
      formatY={(v) => fmt(v, { maximumFractionDigits: 6 })}
      id="cap-rc-graph"
      label={strings.rcGraphLabel}
      xRange={[0, toX(SPANS * tau)]}
      xTitle={t(strings.rcGraphX, { unit: `${xPrefix.symbol}s` })}
      yRange={[0, toY(source)]}
      yTitle={t(strings.rcGraphY, { unit: `${yPrefix.symbol}V` })}
    />
  );
}

function Milestones({
  source,
  tau,
  charging,
  strings,
  fmt,
  sig,
}: {
  source: number;
  tau: number;
  charging: boolean;
  strings: RcStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const fractionOf = (t0: number) => 1 - Math.exp(-t0 / tau);
  const shown = (t0: number) =>
    charging ? fractionOf(t0) : 1 - fractionOf(t0);
  const rows = WHOLE_TAUS.map((n) => [
    t(strings.rcTauRow, { n }),
    withSiPrefix(fmt, n * tau, "s", sig),
    withSiPrefix(
      fmt,
      capacitorVoltage(source, tau, n * tau, charging),
      "V",
      sig
    ),
    percentText(fmt, shown(n * tau)),
  ]);
  const points: [string, number][] = [
    [strings.rcHalfRow, 0.5],
    [strings.rcNinetyRow, 0.9],
    [strings.rcNinetyNineRow, 0.99],
  ];
  const extra = points.map(([label, fraction]) => {
    const when = timeToFraction(tau, fraction) ?? 0;
    return [label, withSiPrefix(fmt, when, "s", sig), "", ""];
  });
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.rcMilestones}</h3>
      <DataTable
        head={[
          strings.rcColumnWhen,
          strings.rcColumnTime,
          strings.rcColumnVoltage,
          strings.rcColumnPercent,
        ]}
        rows={[...rows, ...extra]}
      />
    </div>
  );
}

function RcBody({
  r,
  c,
  v,
  at,
  charging,
  strings,
  fmt,
  sig,
}: {
  r: number | null;
  c: number | null;
  v: number | null;
  at: number | null;
  charging: boolean;
  strings: RcStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  if (r === null || c === null || v === null) {
    return <Notice>{strings.enterAll}</Notice>;
  }
  if (!(r > 0 && c > 0 && v > 0)) {
    return <ErrorNotice>{strings.needPositive}</ErrorNotice>;
  }
  const show = (value: number, unit: string) =>
    withSiPrefix(fmt, value, unit, sig);
  const tau = timeConstant(r, c);
  const timed = at !== null && at >= 0;
  const vcAt = timed ? capacitorVoltage(v, tau, at, charging) : null;
  const lines = [
    t(strings.rcStepTau, {
      r: show(r, "Ω"),
      c: show(c, "F"),
      tau: show(tau, "s"),
    }),
  ];
  if (timed && vcAt !== null) {
    lines.push(
      t(
        charging ? strings.rcStepVoltageCharge : strings.rcStepVoltageDischarge,
        {
          source: show(v, "V"),
          time: show(at, "s"),
          tau: show(tau, "s"),
          vc: show(vcAt, "V"),
        }
      ),
      t(strings.rcStepCurrent, {
        source: show(v, "V"),
        r: show(r, "Ω"),
        time: show(at, "s"),
        tau: show(tau, "s"),
        current: show(rcCurrent(v, r, tau, at), "A"),
      })
    );
  }
  lines.push(
    t(strings.rcStepCorner, {
      r: show(r, "Ω"),
      c: show(c, "F"),
      fc: show(cornerFrequency(r, c), "Hz"),
    })
  );
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.rcTau} value={show(tau, "s")} />
        <StatCard
          label={strings.rcCorner}
          value={show(cornerFrequency(r, c), "Hz")}
        />
        {vcAt === null ? null : (
          <>
            <StatCard label={strings.rcVoltageAt} value={show(vcAt, "V")} />
            <StatCard
              label={strings.rcPercent}
              value={percentText(fmt, vcAt / v)}
            />
            <StatCard
              label={strings.rcCurrentAt}
              value={show(rcCurrent(v, r, tau, at ?? 0), "A")}
            />
          </>
        )}
      </StatGrid>
      {at !== null && at > SPANS * tau ? (
        <Notice>{strings.rcTimeBeyond}</Notice>
      ) : null}
      <Graph
        at={at}
        charging={charging}
        fmt={fmt}
        sig={sig}
        source={v}
        strings={strings}
        tau={tau}
      />
      <WorkingSteps heading={strings.working} lines={lines} />
      <Milestones
        charging={charging}
        fmt={fmt}
        sig={sig}
        source={v}
        strings={strings}
        tau={tau}
      />
    </>
  );
}

export function RcPanel({
  heading,
  strings,
  fmt,
  sig,
}: {
  /** The results heading, written by the widget. */
  heading: string;
  strings: RcStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const [resistance, setResistance] = useState<Entry>({
    value: "10",
    unit: "kΩ",
  });
  const [capacitance, setCapacitance] = useState<Entry>({
    value: "100",
    unit: "uF",
  });
  const [source, setSource] = useState<Entry>({ value: "5", unit: "V" });
  const [time, setTime] = useState<Entry>({ value: "2", unit: "s" });
  const [direction, setDirection] = useState<"charging" | "discharging">(
    "charging"
  );

  return (
    <>
      <PillGroup
        label={strings.rcDirection}
        name="cap-rc-direction"
        onChange={setDirection}
        options={[
          { value: "charging", label: strings.rcCharging },
          { value: "discharging", label: strings.rcDischarging },
        ]}
        value={direction}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityField
          id="cap-rc-r"
          label={strings.rcResistance}
          onChange={(value) => setResistance({ ...resistance, value })}
          onUnitChange={(unit) => setResistance({ ...resistance, unit })}
          unit={resistance.unit}
          unitLabel={t(strings.unitFor, { name: strings.rcResistance })}
          units={OHM_UNITS}
          value={resistance.value}
        />
        <QuantityField
          id="cap-rc-c"
          label={strings.rcCapacitance}
          onChange={(value) => setCapacitance({ ...capacitance, value })}
          onUnitChange={(unit) => setCapacitance({ ...capacitance, unit })}
          unit={capacitance.unit}
          unitLabel={t(strings.unitFor, { name: strings.rcCapacitance })}
          units={FARAD_UNITS}
          value={capacitance.value}
        />
        <QuantityField
          id="cap-rc-v"
          label={strings.rcSource}
          onChange={(value) => setSource({ ...source, value })}
          onUnitChange={(unit) => setSource({ ...source, unit })}
          unit={source.unit}
          unitLabel={t(strings.unitFor, { name: strings.rcSource })}
          units={VOLT_UNITS}
          value={source.value}
        />
        <QuantityField
          id="cap-rc-t"
          label={strings.rcTime}
          onChange={(value) => setTime({ ...time, value })}
          onUnitChange={(unit) => setTime({ ...time, unit })}
          unit={time.unit}
          unitLabel={t(strings.unitFor, { name: strings.rcTime })}
          units={SECOND_UNITS}
          value={time.value}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{heading}</h2>
        <RcBody
          at={read(time, SECOND_UNITS)}
          c={read(capacitance, FARAD_UNITS)}
          charging={direction === "charging"}
          fmt={fmt}
          r={read(resistance, OHM_UNITS)}
          sig={sig}
          strings={strings}
          v={read(source, VOLT_UNITS)}
        />
      </section>
    </>
  );
}
